import { CurrencyPipe, formatDate } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Table } from 'primeng/table';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BaseService } from 'src/app/_services/base.service';
import { FilterServiceService } from 'src/app/_services/filter-service.service';
import { GlobalService } from 'src/app/_services/global.service';
import { InventoryService } from 'src/app/_services/inventory.service';
import { ReportsService } from 'src/app/_services/reports.service';
import { environment } from 'src/environments/environment';

export interface Sales_Report {
  date: string;
  locationId: string;
  locationName: string;
  totalSaleItems: number;
  totalReturnItems: number;
  totalVoidItems: number;
  discount: number;
  salesTax: number;
  nonTaxableTotals: number;
  taxableTotals: number;
  subTotal: number;
  tipAmount: number;
  grandTotal: number;
  tenderedAmount: number;
  totalInvoices: number;
  shippingAmount: number;
  shippingTax: number;
  otherChargesAmount: number;
  netSales: number;
}

@Component({
  selector: 'app-daily-sales-report-v2',
  templateUrl: './daily-sales-report-v2.component.html',
  styleUrls: ['./daily-sales-report-v2.component.scss']
})
export class DailySalesReportV2Component implements OnInit, OnDestroy {
  _globals = GlobalService

  shippingAmountTotal = 0
  dataList: Sales_Report[] = [];
  totalInvoices = 0
  totalSaleItems = 0
  totalReturnItems = 0
  totalVoidItems = 0
  subTotal = 0
  discount = 0
  salesTax = 0
  tipTotalAmount = 0
  totalOtherChargesAmount = 0
  netSalesTotal = 0
  shippingAmount = 0
  shippingTax = 0
  grandTotal = 0
  useNewMobileLayout = false
  totalDetailsExpanded = false
  private detailsExpanded = new Set<number>()
  @ViewChild('dt') dt: Table | undefined;

  colheader = this._abd.reports.filter(x => x.componentName == 'Daily Sales Report')[0].colheader;
  domColHeader = this.colheader[0];
  colflds = this._abd.reports.filter(x => x.componentName == 'Daily Sales Report')[0].colflds;
  summaryHeader = this._abd.reports.filter(x => x.componentName == 'Daily Sales Report')[0].summaryHeader;
  domSummaryHeader = this.summaryHeader[0];
  private allSummaryflds = this._abd.reports.filter(x => x.componentName == 'Daily Sales Report')[0].summaryflds;
  // colflds = ['date', 'locationName', 'totalInvoices', 'totalSaleItems', 'totalVoidItems',
  //   'subTotal', 'discount', 'salesTax', 'grandTotal'];

  exportColumns: any = ''
  locationIds: string[] = [];

  constructor(private _inventoryService: InventoryService,
    private _bs: BaseService,
    private _reportService: ReportsService,
    private _abd: AppBaseDataService,
    private _spinner: NgxSpinnerService,
    private currencyPipe: CurrencyPipe,
    public _filterService: FilterServiceService,
    private route: ActivatedRoute,
    private _router: Router,
  ) {
    this.exportColumns = this.csvcols.map(col => ({ title: col.header, dataKey: col.field }))
  }

  /** Same source as the Hardware/Online-Store menu gating in app-base-data.service.ts. */
  get sellsOnline(): boolean {
    const setup = this._globals.userInfo?.businessSetup?.[0];
    return !!(setup?.standardOnlineStore ?? setup?.StandardOnlineStore);
  }

  get sellsInStore(): boolean {
    const setup = this._globals.userInfo?.businessSetup?.[0];
    return !!(setup?.hasPOS ?? setup?.HasPOS ?? setup?.hasPOSLite ?? setup?.HasPOSLite);
  }

  /** Business only has the Online Store channel set up — no register ever collects tips. */
  get isOnlineOnly(): boolean {
    return this.sellsOnline && !this.sellsInStore
  }

  get csvcols() {
    const cols = this._abd.makeCsvCols(this.colheader, this.colflds)
    return this.isOnlineOnly ? cols.filter(col => col.field !== 'tipAmount') : cols
  }

  get domSummaryflds() {
    return this.isOnlineOnly ? this.allSummaryflds.filter(f => f !== 'tipAmount') : this.allSummaryflds
  }

  ngOnInit(): void {
    this._globals.componentName = 'Daily Sales Report'
    this.updateLayoutFlags()
    this._abd.selectedProductSellTypeFilter = { Type: "All", id: 3, Value: 'All' }
    this.applyRouteDateRange()

    if(!this._filterService.dateRange || this._filterService.dateRange.length<1)
    this._filterService.initDates()

    this._filterService.selectedLocation = this._abd.locations;
    this.getDataList()
  }

  ngOnDestroy() {
    this._globals.componentName = ''
  }

  @HostListener('window:resize')
  onResize() {
    this.updateLayoutFlags()
  }

  private updateLayoutFlags() {
    this.useNewMobileLayout = window.innerWidth < 768
  }

  private applyRouteDateRange() {
    const queryParamMap = this.route.snapshot.queryParamMap
    const startDateParam = queryParamMap.get('startDate')
    const endDateParam = queryParamMap.get('endDate')
    const rangeParam = (queryParamMap.get('range') || '').trim().toLowerCase()

    const startDate = this.parseRouteDate(startDateParam)
    const endDate = this.parseRouteDate(endDateParam)

    if (startDate && endDate) {
      this._filterService.dateRange = [startDate, endDate]
      return
    }

    const today = new Date()
    if (rangeParam === 'month') {
      this._filterService.dateRange = [
        new Date(today.getFullYear(), today.getMonth(), 1),
        new Date(today.getFullYear(), today.getMonth() + 1, 0)
      ]
      return
    }

    if (rangeParam === 'year') {
      this._filterService.dateRange = [
        new Date(today.getFullYear(), 0, 1),
        new Date(today.getFullYear(), 11, 31)
      ]
      return
    }

    if (rangeParam === 'today') {
      const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      this._filterService.dateRange = [currentDay, currentDay]
    }
  }

  private parseRouteDate(value: string | null): Date | null {
    if (!value) {
      return null
    }

    const parsedDate = new Date(value)
    if (Number.isNaN(parsedDate.getTime())) {
      return null
    }

    return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate())
  }

  getLocationsList() {
    let self = this;
    this.locationIds = [];
    let selLocation = this._filterService.selectedLocation;
    selLocation = (typeof selLocation != 'undefined' && selLocation instanceof Array) ? selLocation : [selLocation];
    selLocation.map(function (value: { locationId: { toString: () => string; }; }) {
      self.locationIds.push(value.locationId.toString())
    })
  }

  getDataList() {
    this.getLocationsList();

    const data = {
      "ReportName": 'SaleReport',
      "Parameter": {
        "StartDate": formatDate(this._filterService.dateRange[0], 'MM/dd/yyyy', 'en-US'),
        "EndDate": formatDate(this._filterService.dateRange[1], 'MM/dd/yyyy', 'en-US'),
        "LocationIds": this.locationIds,
        'OrderPlacement': this._filterService.selectedProductSellTypeFilter?.Value
      }
    }

    const url = this._abd.reports.filter(x => x.componentName == 'Daily Sales Report')[0].reportURL;
    this._abd.showSpinner();
    this._bs.httpPost(url, JSON.stringify(data)).subscribe(res => {
      this.dataList = res.data;
      this.dataList.map((item) => item.netSales = item.subTotal - item.discount )
      this._abd.hideSpinner();
      this.shippingAmountTotal = 0
      this.totalInvoices = 0
      this.totalInvoices = 0
      this.totalSaleItems = 0
      this.totalReturnItems = 0
      this.totalVoidItems = 0
      this.subTotal = 0
      this.discount = 0
      this.salesTax = 0
      this.shippingAmount = 0
      this.shippingTax = 0
      this.grandTotal = 0
      this.tipTotalAmount = 0
      this.totalOtherChargesAmount = 0
      this.netSalesTotal = 0
      this.detailsExpanded.clear()
      this.totalDetailsExpanded = false
      for (let i = 0; i < this.dataList.length; i++) {
        let obj = this.dataList[i]
        this.shippingAmountTotal += obj.shippingAmount
        this.totalInvoices += obj.totalInvoices
        this.totalSaleItems += obj.totalSaleItems
        this.totalReturnItems += obj.totalReturnItems || 0
        this.totalVoidItems += obj.totalVoidItems
        this.subTotal += obj.subTotal

        this.discount += obj.discount
        this.salesTax += obj.salesTax
        this.shippingAmount += obj.shippingAmount
        this.shippingTax += obj.shippingTax
        this.totalOtherChargesAmount += obj.otherChargesAmount || 0
        this.tipTotalAmount += obj.tipAmount || 0
        this.netSalesTotal += obj.subTotal - obj.discount
        this.grandTotal += obj.grandTotal
      }
    })
  }

  filterButtonPressed() {
    // alert(this._abd.selectedProductSellTypeFilter.Type)
    this.getDataList()
  }


  callbackFuncFromFilter(event) {

    this.getDataList()
  }

  private getExportColHeaderAndFlds() {
    let colheader = this.summaryHeader
    let colflds = this.colflds
    if (this.isOnlineOnly) {
      const tipIndex = colflds.indexOf('tipAmount')
      colflds = colflds.filter(f => f !== 'tipAmount')
      colheader = colheader.map(row => row.filter((_, i) => i !== tipIndex))
    }
    return { colheader, colflds }
  }

  exportExcel() {
    this.colflds[this.colflds.findIndex(f=> f == "netSale")] = "netSales";
    const { colheader, colflds } = this.getExportColHeaderAndFlds()
    this._abd.exportExcel(this.dataList, colheader[0], colflds, 'Daily Sales Report')
  }

  exportPdf() {
    console.log(this.summaryHeader, this.colflds)
    this.colflds[this.colflds.findIndex(f=> f == "netSale")] = "netSales";
    const { colheader, colflds } = this.getExportColHeaderAndFlds()
    this._abd.exportPdf(this.dataList, colheader, colflds, 'Daily Sales Report', true)
  }

  exportCsv() {
    this.dt?.exportCSV()
  }

  calculateSummaryTotal(col: string) {
    let total = "0";
    if (col.includes("Count") || col.includes("Item") || col.includes("item") || col.toLocaleLowerCase().includes("totalinvoices")) {
      total = this.dataList.reduce((sum, item) => sum + item[col], 0).toFixed(0);
    }
    else if (col == 'netSales') {
      total = GlobalService.formatNumberWithCurrencySign(
        this.dataList.reduce((sum, item) => sum + (item['subTotal'] - item['discount']), 0)
      );
    }
    else {
      total = GlobalService.formatNumberWithCurrencySign(
        this.dataList.reduce((sum, item) => sum + item[col], 0)
      );
    }
    return total;

  }

  toggleDetails(index: number) {
    if (this.detailsExpanded.has(index)) {
      this.detailsExpanded.delete(index)
      return
    }
    this.detailsExpanded.add(index)
  }

  isDetailsExpanded(index: number): boolean {
    return this.detailsExpanded.has(index)
  }

  toggleTotalDetails() {
    this.totalDetailsExpanded = !this.totalDetailsExpanded
  }

}
